/**
 * Shop navigation menu
 * Keeps the referenced navigation columns usable with pointer and keyboard input.
 */

class ShopNavigationMenu {
  constructor() {
    this.menuReferences = Array.from(document.querySelectorAll('[data-menu-reference="shop-nav"]'));
    this.activeColumn = null;
    this.activeMenuItem = null;
    this.hideTimer = null;

    this.init();
  }

  init() {
    if (this.menuReferences.length === 0) return;

    this.menuReferences.forEach((menuItem) => {
      const referencedColumns = this.findReferencedColumns(menuItem);
      if (!referencedColumns) return;

      menuItem.addEventListener('mouseenter', () => this.showReferencedMenu(menuItem, referencedColumns));
      menuItem.addEventListener('focus', () => this.showReferencedMenu(menuItem, referencedColumns));
      menuItem.addEventListener('click', (event) => {
        if (menuItem.getAttribute('href')?.startsWith('#')) event.preventDefault();
        this.showReferencedMenu(menuItem, referencedColumns);
      });
      menuItem.addEventListener('mouseleave', () => this.scheduleDefaultMenu());
      menuItem.addEventListener('blur', () => this.scheduleDefaultMenu());

      referencedColumns.addEventListener('mouseenter', () => this.cancelScheduledMenuChange());
      referencedColumns.addEventListener('focusin', () => this.cancelScheduledMenuChange());
      referencedColumns.addEventListener('mouseleave', () => this.scheduleDefaultMenu());
      referencedColumns.addEventListener('focusout', () => this.scheduleDefaultMenu());
    });

    document.querySelectorAll('.mega-menu__grid').forEach((menuGrid) => {
      menuGrid.querySelectorAll('a:not([data-menu-reference="shop-nav"])').forEach((link) => {
        if (link.closest('.mega-menu__shop-nav-columns')) return;
        link.addEventListener('mouseenter', () => this.resetToDefault(menuGrid));
        link.addEventListener('focus', () => this.resetToDefault(menuGrid));
      });

      this.resetToDefault(menuGrid);
    });

    document.addEventListener('header-menu:deactivate', () => this.resetAllToDefault());
    document.addEventListener('keydown', (event) => this.handleEscape(event));
  }

  /**
   * @param {HTMLElement} menuItem
   * @returns {Element | null}
   */
  findReferencedColumns(menuItem) {
    const menuHandle = menuItem.getAttribute('data-nav-menu-handle');
    const menuGrid = menuItem.closest('.mega-menu__grid');
    if (!menuHandle || !menuGrid) return null;

    return menuGrid.querySelector(
      `.mega-menu__shop-nav-columns[data-nav-menu-handle="${CSS.escape(menuHandle)}"]`
    );
  }

  /**
   * @param {HTMLElement} menuItem
   * @param {HTMLElement} referencedColumns
   */
  showReferencedMenu(menuItem, referencedColumns) {
    this.cancelScheduledMenuChange();
    this.hideMenusInGrid(menuItem.closest('.mega-menu__grid'));

    referencedColumns.style.display = 'flex';
    referencedColumns.setAttribute('aria-hidden', 'false');
    menuItem.setAttribute('aria-expanded', 'true');
    menuItem.parentElement?.classList.add('is-expanded');

    this.activeColumn = referencedColumns;
    this.activeMenuItem = menuItem;
  }

  scheduleDefaultMenu() {
    this.cancelScheduledMenuChange();
    this.hideTimer = window.setTimeout(() => {
      const activeElement = document.activeElement;
      const pointerStillInside = this.activeColumn?.matches(':hover') || this.activeMenuItem?.matches(':hover');
      const focusStillInside =
        activeElement instanceof Node &&
        (this.activeColumn?.contains(activeElement) || this.activeMenuItem === activeElement);

      if (pointerStillInside || focusStillInside) return;

      const menuGrid = this.activeMenuItem?.closest('.mega-menu__grid');
      if (menuGrid) this.resetToDefault(menuGrid);
    }, 120);
  }

  cancelScheduledMenuChange() {
    if (this.hideTimer === null) return;
    window.clearTimeout(this.hideTimer);
    this.hideTimer = null;
  }

  /** @param {Element | null} menuGrid */
  hideMenusInGrid(menuGrid) {
    if (!menuGrid) return;

    menuGrid.querySelectorAll('.mega-menu__shop-nav-columns').forEach((column) => {
      column.style.display = 'none';
      column.setAttribute('aria-hidden', 'true');
    });

    menuGrid.querySelectorAll('[data-menu-reference="shop-nav"]').forEach((menuItem) => {
      menuItem.setAttribute('aria-expanded', 'false');
      menuItem.parentElement?.classList.remove('is-expanded');
    });
  }

  /** @param {Element | null} menuGrid */
  resetToDefault(menuGrid) {
    if (!menuGrid) return;

    this.hideMenusInGrid(menuGrid);

    const defaultColumn = menuGrid.querySelector('[data-default-shop-navigation]');
    const menuHandle = defaultColumn?.getAttribute('data-nav-menu-handle');
    const defaultMenuItem = menuHandle
      ? menuGrid.querySelector(
          `[data-menu-reference="shop-nav"][data-nav-menu-handle="${CSS.escape(menuHandle)}"]`
        )
      : null;

    if (!(defaultColumn instanceof HTMLElement) || !(defaultMenuItem instanceof HTMLElement)) return;

    defaultColumn.style.display = 'flex';
    defaultColumn.setAttribute('aria-hidden', 'false');
    defaultMenuItem.setAttribute('aria-expanded', 'true');
    defaultMenuItem.parentElement?.classList.add('is-expanded');

    this.activeColumn = defaultColumn;
    this.activeMenuItem = defaultMenuItem;
  }

  resetAllToDefault() {
    document.querySelectorAll('.mega-menu__grid').forEach((menuGrid) => this.resetToDefault(menuGrid));
  }

  /** @param {KeyboardEvent} event */
  handleEscape(event) {
    if (event.key !== 'Escape' || !this.activeMenuItem) return;

    event.preventDefault();
    const menuItem = this.activeMenuItem;
    const menuGrid = menuItem.closest('.mega-menu__grid');
    this.resetToDefault(menuGrid);
    menuItem.focus();
  }
}

const initializeShopNavigation = () => new ShopNavigationMenu();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeShopNavigation, { once: true });
} else {
  initializeShopNavigation();
}
